import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import icons from '../../../../constants/icons';
import type { FetchedGame, ImportedGame } from './steamGameImportTypes';

export interface SteamGameImportStatusTableProps {
  importedGames: ImportedGame[];
  fetchedGames: FetchedGame[];
}

function SteamGameImportStatusTable({
  importedGames,
  fetchedGames,
}: SteamGameImportStatusTableProps) {
  const { t } = useTranslation();

  return (
    <div
      className="card glass-panel p-4 d-flex flex-column flex-grow-1 overflow-hidden"
      style={{ minHeight: 0 }}
    >
      <div className="d-flex align-items-center mb-3 flex-shrink-0">
        <FontAwesomeIcon icon={icons.LIST_CHECK} className="text-blue me-2" />
        <h5 className="mb-0">{t('Import Status Tracking')}</h5>
        <span className="badge bg-dark border border-secondary text-secondary ms-auto">
          {t('{{imported}} / {{total}} Titles', {
            imported: importedGames.filter((g) => g.status === 'complete')
              .length,
            total: fetchedGames.length,
          })}
        </span>
      </div>

      <div className="table-responsive overflow-y-auto flex-grow-1 overflow-x-hidden">
        <table className="table table-dark table-hover align-middle mb-0 border-0">
          <thead className="sticky-top bg-dark">
            <tr>
              <th className="bg-transparent text-secondary small text-uppercase">
                {t('Title')}
              </th>
              <th className="bg-transparent text-secondary small text-uppercase">
                {t('App Id')}
              </th>
              <th className="bg-transparent text-secondary small text-uppercase text-center">
                {t('SKUs')}
              </th>
              <th
                className="bg-transparent text-secondary small text-uppercase text-end"
                style={{ width: '100px' }}
              >
                {t('Status')}
              </th>
            </tr>
          </thead>
          <tbody>
            <AnimatePresence>
              {importedGames.map((game) => (
                <motion.tr
                  key={game.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="border-bottom border-secondary-subtle"
                >
                  <td>
                    <div className="d-flex align-items-center">
                      <span
                        className={`pulse-indicator ${game.status === 'complete' ? 'bg-success' : 'pulse-active'}`}
                      ></span>
                      <span className="fw-medium">{game.name}</span>
                    </div>
                  </td>
                  <td>
                    <code className="text-blue">{game.appId}</code>
                  </td>
                  <td className="text-center">
                    <span className="badge bg-secondary-subtle text-light">
                      {game.skuCount}
                    </span>
                  </td>
                  <td className="text-end">
                    {game.status === 'complete' ? (
                      <span className="text-success small">
                        <FontAwesomeIcon
                          icon={icons.CHECK_DOUBLE}
                          className="me-1"
                        />
                        {t('Synced')}
                      </span>
                    ) : (
                      <span className="text-blue small">
                        <FontAwesomeIcon
                          icon={icons.SPINNER}
                          spin
                          className="me-1"
                        />
                        {t('Importing')}
                      </span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </AnimatePresence>
            {importedGames.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-5 text-secondary">
                  <div className="py-4">
                    <FontAwesomeIcon
                      icon={icons.SATELLITE_DISH}
                      size="3x"
                      className="mb-3 d-block text-blue opacity-50"
                    />
                    <p className="mb-0">{t('Awaiting Steam Data Stream...')}</p>
                    <div className="small opacity-50 mt-2">
                      {t('Initialize sync to begin importing records')}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SteamGameImportStatusTable;
